import { useState, useEffect } from 'react';
import axios from 'axios';
import { DateField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;

const ZONE_COLORS = {
  O: { fill: '#E8D5E8', label: 'Obesa' },
  S: { fill: '#D5C8E0', label: 'Sobrepeso' },
  A: { fill: '#C8D8E8', label: 'Adequado' },
  BP: { fill: '#D8E8F0', label: 'Baixo peso' },
};

const CLASS_LABELS = { BP: 'Baixo peso', A: 'Adequado', S: 'Sobrepeso', O: 'Obesa' };
const CLASS_COLORS = { BP: '#6B9BD2', A: '#5B8A6B', S: '#9B7BBD', O: '#BD7B8A' };

export default function AtalahChart({ patientId, isClient, editable = false, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editField, setEditField] = useState(null); // 'dum' | 'altura' | null
  const [editValue, setEditValue] = useState('');
  const [savingField, setSavingField] = useState(false);

  const loadData = async () => {
    try {
      const url = isClient ? `${API}/api/my-atalah-curve` : `${API}/api/patients/${patientId}/atalah-curve`;
      const { data: d } = await axios.get(url);
      setData(d);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, isClient]);

  const saveField = async () => {
    if (!editValue) return;
    setSavingField(true);
    try {
      const payload = editField === 'altura' ? { altura: parseFloat(editValue) } : { dum: editValue };
      await axios.put(`${API}/api/patients/${patientId}`, payload);
      setEditField(null); setEditValue('');
      await loadData();
      if (onUpdated) onUpdated();
    } catch {} finally { setSavingField(false); }
  };

  if (loading) return <div className="text-center py-4 text-brand-muted text-xs">A carregar curva...</div>;
  if (!data) return <div className="text-center py-4 text-brand-muted text-xs" data-testid="atalah-chart">Sem dados disponíveis</div>;

  const boundaries = data.boundaries || {};
  const points = data.points || [];
  const missingDum = !data.dum;
  const missingAltura = !data.altura;
  const warning = data.error
    ? data.error
    : (points.length === 0 ? 'Ainda não há registos de peso (check-in, InBody/Prozis ou avaliação física) dentro do período gestacional para traçar a curva.' : null);

  // Chart dimensions
  const W = 700, H = 400;
  const pad = { top: 30, right: 30, bottom: 50, left: 45 };
  const cw = W - pad.left - pad.right;
  const ch = H - pad.top - pad.bottom;

  const minWeek = 6, maxWeek = 40;
  const minIMC = 17, maxIMC = 40;

  const x = (week) => pad.left + ((week - minWeek) / (maxWeek - minWeek)) * cw;
  const y = (imc) => pad.top + ((maxIMC - imc) / (maxIMC - minIMC)) * ch;

  // Build boundary lines
  const weeks = [];
  for (let w = minWeek; w <= maxWeek; w++) weeks.push(w);

  const lineBP = weeks.map(w => `${x(w)},${y(boundaries[w]?.bp || 20)}`).join(' ');
  const lineA = weeks.map(w => `${x(w)},${y(boundaries[w]?.a || 25)}`).join(' ');
  const lineS = weeks.map(w => `${x(w)},${y(boundaries[w]?.s || 30)}`).join(' ');

  // Zone polygons (bottom to top: BP below lineBP, A between BP-A, S between A-S, O above S)
  const bottomY = y(minIMC);
  const topY = y(maxIMC);

  const polyBP = weeks.map(w => `${x(w)},${y(boundaries[w]?.bp || 20)}`).join(' ')
    + ` ${x(maxWeek)},${bottomY} ${x(minWeek)},${bottomY}`;
  const polyA = weeks.map(w => `${x(w)},${y(boundaries[w]?.a || 25)}`).join(' ')
    + ' ' + [...weeks].reverse().map(w => `${x(w)},${y(boundaries[w]?.bp || 20)}`).join(' ');
  const polyS = weeks.map(w => `${x(w)},${y(boundaries[w]?.s || 30)}`).join(' ')
    + ' ' + [...weeks].reverse().map(w => `${x(w)},${y(boundaries[w]?.a || 25)}`).join(' ');
  const polyO = weeks.map(w => `${x(w)},${topY}`).join(' ')
    + ' ' + [...weeks].reverse().map(w => `${x(w)},${y(boundaries[w]?.s || 30)}`).join(' ');

  const currentClass = data.current_classification;
  const lastPoint = points.length > 0 ? points[points.length - 1] : null;

  return (
    <div data-testid="atalah-chart">
      <p className="font-heading font-medium text-sm text-brand-text mb-1">Curva de Ganho de Peso Gestacional</p>
      <p className="text-[10px] text-brand-muted mb-3">Atalah et al. (1997). IMC por semana de gestação.</p>

      {warning && (
        <div className="mb-3 p-2.5 rounded-xl bg-amber-50 border border-amber-200" data-testid="atalah-warning">
          <div className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">⚠</span>
            <p className="text-[11px] text-amber-700 leading-snug flex-1">{warning}</p>
          </div>
          {editable && (missingDum || missingAltura) && (
            <div className="flex flex-wrap gap-2 mt-2 pl-6">
              {missingDum && editField !== 'dum' && (
                <button onClick={() => { setEditField('dum'); setEditValue(''); }} className="text-[11px] font-medium bg-amber-500 text-white rounded-full px-3 py-1" data-testid="atalah-set-dum-btn">Definir DUM</button>
              )}
              {missingAltura && editField !== 'altura' && (
                <button onClick={() => { setEditField('altura'); setEditValue(''); }} className="text-[11px] font-medium bg-amber-500 text-white rounded-full px-3 py-1" data-testid="atalah-set-altura-btn">Registar altura</button>
              )}
            </div>
          )}
          {editable && editField && (
            <div className="flex items-center gap-2 mt-2 pl-6">
              {editField === 'dum' ? (
                <DateField value={editValue} onChange={e => setEditValue(e.target.value)} className="text-xs border border-amber-300 rounded-lg px-2 py-1" data-testid="atalah-dum-input" />
              ) : (
                <input type="number" step="0.1" placeholder="Altura (cm)" value={editValue} onChange={e => setEditValue(e.target.value)} className="text-xs border border-amber-300 rounded-lg px-2 py-1 w-28" data-testid="atalah-altura-input" />
              )}
              <button onClick={saveField} disabled={savingField || !editValue} className="text-[11px] font-medium bg-brand-green text-white rounded-full px-3 py-1 disabled:opacity-50" data-testid="atalah-save-field-btn">{savingField ? 'A guardar...' : 'Guardar'}</button>
              <button onClick={() => { setEditField(null); setEditValue(''); }} className="text-[11px] text-amber-700">Cancelar</button>
            </div>
          )}
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[500px]" style={{ maxHeight: 350 }}>
          {/* Zone fills */}
          <polygon points={polyBP} fill={ZONE_COLORS.BP.fill} opacity="0.6" />
          <polygon points={polyA} fill={ZONE_COLORS.A.fill} opacity="0.6" />
          <polygon points={polyS} fill={ZONE_COLORS.S.fill} opacity="0.6" />
          <polygon points={polyO} fill={ZONE_COLORS.O.fill} opacity="0.6" />

          {/* Boundary lines */}
          <polyline points={lineBP} fill="none" stroke="#6B9BD2" strokeWidth="1.5" />
          <polyline points={lineA} fill="none" stroke="#5B8A6B" strokeWidth="1.5" />
          <polyline points={lineS} fill="none" stroke="#9B7BBD" strokeWidth="1.5" />

          {/* Grid lines */}
          {[20, 25, 30, 35, 40].map(imc => (
            <line key={imc} x1={pad.left} y1={y(imc)} x2={W - pad.right} y2={y(imc)} stroke="#E5E7E3" strokeWidth="0.5" strokeDasharray="3,3" />
          ))}
          {[10, 15, 20, 25, 30, 35, 40].map(w => (
            <line key={w} x1={x(w)} y1={pad.top} x2={x(w)} y2={H - pad.bottom} stroke="#E5E7E3" strokeWidth="0.5" strokeDasharray="3,3" />
          ))}

          {/* Zone labels */}
          <text x={x(28)} y={y(19)} textAnchor="middle" fontSize="11" fill="#6B9BD2" fontWeight="600" opacity="0.7">BP</text>
          <text x={x(28)} y={y(23.5)} textAnchor="middle" fontSize="11" fill="#5B8A6B" fontWeight="600" opacity="0.7">A</text>
          <text x={x(28)} y={y(28.5)} textAnchor="middle" fontSize="11" fill="#9B7BBD" fontWeight="600" opacity="0.7">S</text>
          <text x={x(28)} y={y(34)} textAnchor="middle" fontSize="11" fill="#BD7B8A" fontWeight="600" opacity="0.7">O</text>

          {/* Axes labels */}
          <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="#7A8276">Semanas de gestação</text>
          <text x={12} y={H / 2} textAnchor="middle" fontSize="10" fill="#7A8276" transform={`rotate(-90, 12, ${H / 2})`}>IMC</text>

          {/* X axis ticks */}
          {[6, 10, 14, 18, 22, 26, 30, 34, 38, 40].map(w => (
            <g key={w}>
              <line x1={x(w)} y1={H - pad.bottom} x2={x(w)} y2={H - pad.bottom + 4} stroke="#7A8276" strokeWidth="0.5" />
              <text x={x(w)} y={H - pad.bottom + 14} textAnchor="middle" fontSize="8" fill="#7A8276">{w}</text>
            </g>
          ))}

          {/* Y axis ticks */}
          {[18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40].map(imc => (
            <g key={imc}>
              <line x1={pad.left - 4} y1={y(imc)} x2={pad.left} y2={y(imc)} stroke="#7A8276" strokeWidth="0.5" />
              <text x={pad.left - 7} y={y(imc) + 3} textAnchor="end" fontSize="8" fill="#7A8276">{imc}</text>
            </g>
          ))}

          {/* Data points line */}
          {points.length > 1 && (
            <polyline
              points={points.map(p => `${x(p.week)},${y(p.imc)}`).join(' ')}
              fill="none" stroke="#657161" strokeWidth="2" strokeLinejoin="round"
            />
          )}

          {/* Data points */}
          {points.map((p, i) => {
            const isLast = i === points.length - 1;
            return (
              <g key={i}>
                <circle cx={x(p.week)} cy={y(p.imc)} r={isLast ? 6 : 3.5}
                  fill={isLast ? '#657161' : '#fff'} stroke="#657161" strokeWidth={isLast ? 2.5 : 1.5} />
                {isLast && (
                  <text x={x(p.week)} y={y(p.imc) - 10} textAnchor="middle" fontSize="8" fill="#657161" fontWeight="600">
                    {p.imc}
                  </text>
                )}
              </g>
            );
          })}

          {/* Border */}
          <rect x={pad.left} y={pad.top} width={cw} height={ch} fill="none" stroke="#E5E7E3" strokeWidth="1" />
        </svg>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 mt-3 flex-wrap">
        {Object.entries(ZONE_COLORS).map(([key, val]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: val.fill, border: `1px solid ${CLASS_COLORS[key]}` }} />
            <span className="text-[10px] text-brand-muted"><strong>{key}</strong> {val.label}</span>
          </div>
        ))}
      </div>

      {/* Current classification */}
      {currentClass && lastPoint && (
        <div className="mt-3 p-3 rounded-xl text-center" style={{ backgroundColor: ZONE_COLORS[currentClass]?.fill || '#F7F8F6' }} data-testid="atalah-classification">
          <p className="text-xs text-brand-muted">Classificação atual (semana {lastPoint.week_int})</p>
          <p className="text-sm font-heading font-semibold" style={{ color: CLASS_COLORS[currentClass] || '#2D332B' }}>
            {CLASS_LABELS[currentClass] || currentClass}
          </p>
          <p className="text-[10px] text-brand-muted mt-0.5">IMC: {lastPoint.imc} | Peso: {lastPoint.peso} kg</p>
        </div>
      )}
    </div>
  );
}
